// src/admin/components/assets/AssetFormFields.jsx
import React from 'react';
import PropTypes from 'prop-types';
import EditableField from '../../../shared/components/EditableField';
import FormInput from '../../../shared/components/FormInput';

/**
 * AssetFormFields - Reusable asset form fields component
 * 
 * Displays asset information fields in a 2-column grid layout.
 * Can be used in create mode (all inputs) or edit mode (editable display fields).
 * Use compact=true for simplified view (task wizard) without serial/warranty fields.
 */
export default function AssetFormFields({ assetForm, onChange, mode = 'edit', compact = false, readOnlyExtId = false }) {
  const handleChange = (field, value) => {
    onChange({ ...assetForm, [field]: value });
  };

  // In create mode, use regular inputs; in edit mode, use EditableField
  const renderField = (field, label, placeholder, options = {}) => {
    const { type = 'text', required = false } = options;
    
    if (mode === 'create') {
      return (
        <FormInput
          key={field}
          id={field}
          type={type}
          label={label}
          placeholder={placeholder}
          value={assetForm[field] || ''}
          onChange={e => handleChange(field, e.target.value)}
          required={required}
        />
      );
    }

    return (
      <EditableField
        key={field}
        label={label}
        name={field}
        value={assetForm[field] || ''}
        placeholder={placeholder}
        onChange={val => handleChange(field, val)}
        type={type}
      />
    );
  };

  // External ID field - can be read-only (display only) or editable
  const renderExternalIdField = () => {
    // Read-only mode: show as plain text (or skip if empty)
    if (readOnlyExtId) {
      if (!assetForm.asset_ext_id) return null;
      return (
        <div className="mb-3">
          <span className="form-label-caps small text-muted d-block">External ID</span>
          <span className="text-body">{assetForm.asset_ext_id}</span>
        </div>
      );
    }

    // Editable mode: regular input
    return (
      <FormInput
        id="asset_ext_id"
        label={(
          <>
            External ID
            <span className="text-muted ms-1" style={{ fontSize: '0.75rem' }}>(optional)</span>
          </>
        )}
        placeholder="Enter external ID"
        value={assetForm.asset_ext_id || ''}
        onChange={e => handleChange('asset_ext_id', e.target.value)}
      />
    );
  };

  return (
    <div className="row gx-4">
      <div className="col-md-6">
        {renderField('asset_name', 'Asset Name', 'Enter asset name', { required: true })}
        {renderField('asset_make', 'Make', 'Enter make')}
        {renderField('asset_model', 'Model', 'Enter model')}
        {!compact && renderField('asset_serial_number', 'Serial Number', 'Enter serial number')}
      </div>
      <div className="col-md-6">
        {renderField('street_address', 'Address', 'Enter address')}
        {renderField('city', 'City', 'Enter city')}
        {!compact && renderField('asset_warranty_end_date', 'Warranty End Date', 'YYYY-MM-DD', { type: 'date' })}
        {renderExternalIdField()}
      </div>
    </div>
  );
}

AssetFormFields.propTypes = {
  assetForm: PropTypes.shape({
    asset_name: PropTypes.string,
    asset_make: PropTypes.string,
    asset_model: PropTypes.string,
    asset_serial_number: PropTypes.string,
    asset_warranty_end_date: PropTypes.string,
    asset_ext_id: PropTypes.string,
    street_address: PropTypes.string,
    city: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['create', 'edit']),
  compact: PropTypes.bool,
  readOnlyExtId: PropTypes.bool,
};

AssetFormFields.defaultProps = {
  mode: 'edit',
  compact: false,
  readOnlyExtId: false,
};
